import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  ButtonGroup
} from '@mui/material';

interface WeightInputModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (weight: number, mode: 'add' | 'update') => void;
  selectedProduct: {
    productInfo: {
      id: string;
      name: string;
      price: number;
      vatRate: number;
      unit: string;
    },
    itemCanBeNegative: boolean;
    itemStockQuantity: number;
    currentQuantity?: number;
  } | null;
  isCartUpdate?: boolean;
}

const WeightInputModal: React.FC<WeightInputModalProps> = ({ 
  open, 
  onClose, 
  onConfirm, 
  selectedProduct,
  isCartUpdate = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [mode, setMode] = useState<'add' | 'update'>('update');

  // Modal açıldığında input değerini ayarla
  useEffect(() => {
    if (open) {
      // Eğer sepet güncellemesiyse mevcut miktarı göster
      if (isCartUpdate && selectedProduct?.currentQuantity !== undefined) {
        setInputValue(selectedProduct.currentQuantity.toString());
      } else {
        setInputValue('');
      }
      setIsProcessing(false);
      setMode('update'); // Varsayılan mod: güncelle
    }
  }, [open, selectedProduct, isCartUpdate]);

  const handleNumpadClick = (value: string) => {
    if (value === 'C') {
      setInputValue('');
    } else if (value === '<') {
      setInputValue(prev => prev.slice(0, -1));
    } else if (value === 'Enter') {
      handleConfirm();
    } else if (value === '.' || value === ',') {
      // Nokta veya virgül tuşuna basıldığında
      if (!inputValue.includes('.')) {
        setInputValue(prev => prev + '.');
      }
    } else if (/[0-9]/.test(value)) {
      setInputValue(prev => prev + value);
    }
  };

  const handleConfirm = () => {
    if (inputValue && parseFloat(inputValue) > 0 && !isProcessing) {
      setIsProcessing(true); // İşlem başladı, butonları devre dışı bırak
      const weightValue = Math.max(0.01, parseFloat(inputValue) || 0.01);
      console.log('Miktar değeri onaylandı:', weightValue, 'Mod:', mode);
      onConfirm(weightValue, mode);
    }
  };

  const title = isCartUpdate 
    ? "Ürün Miktarını Güncelle" 
    : "Kg Ürün Miktarı Girin";

  const unitText = selectedProduct?.productInfo.unit 
    ? selectedProduct.productInfo.unit 
    : 'Kg';

  return (
    <Dialog 
      open={open} 
      onClose={() => {
        if (!isProcessing) {
          onClose();
        }
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        {title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {selectedProduct?.productInfo.name || 'Ürün'}
          </Typography>
          
          {isCartUpdate && selectedProduct?.currentQuantity !== undefined && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Mevcut miktar: {selectedProduct.currentQuantity} {unitText}
            </Typography>
          )}
          
          <TextField
            fullWidth
            label={`Miktar (${unitText})`}
            type="text"
            value={inputValue}
            onChange={(e) => {
              // Sadece sayı ve nokta girişine izin ver
              const value = e.target.value;
              if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                setInputValue(value);
              }
            }}
            inputProps={{ 
              inputMode: "decimal",
              pattern: "[0-9]*\\.?[0-9]*"
            }}
            sx={{ mb: 2 }}
          />
          
          {isCartUpdate && (
            <ButtonGroup fullWidth variant="outlined" sx={{ mb: 2 }}>
              <Button 
                color={mode === 'update' ? 'primary' : 'inherit'}
                variant={mode === 'update' ? 'contained' : 'outlined'}
                onClick={() => setMode('update')}
                disabled={isProcessing}
              >
                Miktarı Güncelle
              </Button>
              <Button 
                color={mode === 'add' ? 'primary' : 'inherit'}
                variant={mode === 'add' ? 'contained' : 'outlined'}
                onClick={() => setMode('add')}
                disabled={isProcessing}
              >
                Miktar Ekle
              </Button>
            </ButtonGroup>
          )}
          
          {/* Numpad */}
          <Grid container spacing={1}>
            {['7', '8', '9'].map(num => (
              <Grid item xs={4} key={num}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={() => handleNumpadClick(num)}
                  disabled={isProcessing}
                >
                  {num}
                </Button>
              </Grid>
            ))}
            {['4', '5', '6'].map(num => (
              <Grid item xs={4} key={num}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={() => handleNumpadClick(num)}
                  disabled={isProcessing}
                >
                  {num}
                </Button>
              </Grid>
            ))}
            {['1', '2', '3'].map(num => (
              <Grid item xs={4} key={num}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={() => handleNumpadClick(num)}
                  disabled={isProcessing}
                >
                  {num}
                </Button>
              </Grid>
            ))}
            <Grid item xs={4}>
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={() => handleNumpadClick('.')}
                disabled={isProcessing}
              >
                .
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={() => handleNumpadClick('0')}
                disabled={isProcessing}
              >
                0
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button 
                fullWidth 
                variant="outlined" 
                color="error"
                onClick={() => handleNumpadClick('C')}
                disabled={isProcessing}
              >
                C
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          color="inherit"
          disabled={isProcessing}
        >
          İptal
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="primary" 
          variant="contained"
          disabled={!inputValue || parseFloat(inputValue) <= 0 || isProcessing}
        >
          {isCartUpdate ? (mode === 'update' ? 'Güncelle' : 'Ekle') : 'Ekle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeightInputModal; 